/**
 * Pure timescale math for the layout engine. Mirrors
 * `@tento-ui/components/sheet/timescale` — no DOM deps,
 * just `@tento-chrono`. The two copies exist side-by-side until
 * yuzu-ui's layout-touching code (sheet-layout-spec, cell-layout)
 * re-routes through this file (§5.9 M5 follow-up).
 *
 * `RowRenderMode` originated in `tento/tento-lona-js/src/timeline/timescale-utils.ts`
 * and lives here so `LayoutEngine` + `RenderPlan` can depend only
 * on `tento/tento-lona-js/sheets`.
 */

import type { Option } from "../core/option.ts";
import { DateUnit, PartialDate } from "@tento-chrono";

/**
 * A sheet or row time axis (`"day"` … `"year"`), and below, the helpers
 * over it: one name for the type and its namespace, as with `LayoutKind`.
 */
export type Timescale = DateUnit.Type;

/**
 * Values that go with `Timescale`: `parse`, `order`, `conservativeSpanCount`,
 * `stackSize`, `shouldStack`, `shouldSpan`, and 3 more.
 */
export namespace Timescale {
  /**
   * The wire spelling `value` as a `Timescale`, or `null` when it is absent or
   * not one (`"custom"`, a typo). A sheet whose wire `timeScale` does not parse
   * opens at `"day"`.
   */
  export function parse(value: Option<string>): Option<Timescale> {
    switch (value) {
      case "day":
      case "week":
      case "month":
      case "year":
        return value;
      default:
        return null;
    }
  }

  export function order(timescale: Timescale | "custom"): number {
    switch (timescale) {
      case "day":
        return 1;
      case "week":
        return 7;
      case "month":
        return 31;
      case "year":
        return 366;
      case "custom":
        return 1;
    }
  }

  export function conservativeSpanCount(
    rowTimescale: Timescale,
    sheetTimescale: Timescale,
  ): number {
    if (rowTimescale === "week" && sheetTimescale === "day") return 7;
    if (rowTimescale === "month" && sheetTimescale === "day") return 31;
    if (rowTimescale === "year" && sheetTimescale === "day") return 366;
    if (rowTimescale === "month" && sheetTimescale === "week") return 5;
    if (rowTimescale === "year" && sheetTimescale === "week") return 53;
    if (rowTimescale === "year" && sheetTimescale === "month") return 12;
    return 1;
  }

  export function stackSize(
    rowTimescale: Timescale | "custom",
    sheetTimescale: Timescale,
  ): Option<number> {
    const rowOrder = order(rowTimescale);
    const sheetOrder = order(sheetTimescale);
    if (rowOrder >= sheetOrder) return null;
    return Math.ceil(sheetOrder / rowOrder);
  }

  export function shouldStack(
    rowTimescale: Timescale | "custom",
    sheetTimescale: Timescale,
  ): boolean {
    return stackSize(rowTimescale, sheetTimescale) !== null;
  }

  export function shouldSpan(
    rowTimescale: Timescale | "custom",
    sheetTimescale: Timescale | "custom",
  ): boolean {
    if (rowTimescale === "custom" || sheetTimescale === "custom") return false;
    const rowOrder = order(rowTimescale);
    const sheetOrder = order(sheetTimescale);
    return rowOrder > sheetOrder;
  }

  export function isOwnerColumn(
    columnPd: PartialDate,
    rowPd: PartialDate,
  ): boolean {
    return (
      columnPd.start.dse < rowPd.end.dse && rowPd.end.dse <= columnPd.end.dse
    );
  }

  export function ownedRowPeriod(
    columnPd: PartialDate,
    rowTimescale: Timescale,
  ): Option<PartialDate> {
    const columnStart = columnPd.start;
    const startPeriod = PartialDate.fromDateAndType(
      rowTimescale,
      columnStart,
      columnPd.weekStart,
    );
    if (isOwnerColumn(columnPd, startPeriod)) {
      return startPeriod;
    }
    return null;
  }

  export function containingPeriod(
    columnPd: PartialDate,
    rowTimescale: Timescale,
  ): PartialDate {
    const columnStart = columnPd.start;
    return PartialDate.fromDateAndType(
      rowTimescale,
      columnStart,
      columnPd.weekStart,
    );
  }
}

/**
 * Rendering mode for a row in the sheet.
 * - `normal` — row timescale == sheet timescale (1:1 rendering).
 * - `stacked` — row timescale < sheet timescale (e.g. day rows in month view).
 * - `spanned` — row timescale > sheet timescale (e.g. month rows in day view).
 * - `span-stacked` — sheet timescale < `minWidthTimescale`; fixed children
 *   span the min-width period with subcells stacked at the sheet timescale.
 */
export type RowRenderMode =
  | { type: "normal" }
  | { type: "stacked" }
  | { type: "spanned"; isStatic: boolean }
  | { type: "span-stacked"; spanTimescale: Timescale; spanCount: number };

/** Values that go with `RowRenderMode`: `compute`, `equals`. */
export namespace RowRenderMode {
  /**
   * Dispatch priority: span-stacked > stacked > spanned > normal.
   */
  export function compute(
    rowTimescale: Timescale | "custom",
    sheetTimescale: Timescale,
    capabilities: { readonly spannable: boolean; readonly stackable: boolean },
    _visibleWidthPx: number,
    _resolvedWidthPx: number,
    minWidthTimescale: Option<Timescale>,
  ): RowRenderMode {
    if (
      minWidthTimescale &&
      rowTimescale !== "custom" &&
      Timescale.shouldSpan(minWidthTimescale, sheetTimescale) &&
      !Timescale.shouldSpan(rowTimescale, sheetTimescale)
    ) {
      return {
        type: "span-stacked",
        spanTimescale: minWidthTimescale,
        spanCount: Timescale.conservativeSpanCount(minWidthTimescale, sheetTimescale),
      };
    }

    if (capabilities.stackable && Timescale.shouldStack(rowTimescale, sheetTimescale)) {
      return { type: "stacked" };
    }

    if (capabilities.spannable && Timescale.shouldSpan(rowTimescale, sheetTimescale)) {
      return { type: "spanned", isStatic: true };
    }

    return { type: "normal" };
  }

  export function equals(a: RowRenderMode, b: RowRenderMode): boolean {
    if (a.type !== b.type) return false;
    if (a.type === "spanned" && b.type === "spanned") {
      return a.isStatic === b.isStatic;
    }
    if (a.type === "span-stacked" && b.type === "span-stacked") {
      return a.spanTimescale === b.spanTimescale && a.spanCount === b.spanCount;
    }
    return true;
  }
}
